import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  listEmailTemplates,
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
} from "../services/api/email-templates";

const toDraft = (content) => ({
  subject: content.subject ?? "",
  heading: content.heading ?? "",
  body: content.body ?? "",
  buttonLabel: content.buttonLabel ?? "",
});

const sameDraft = (a, b) =>
  a.subject === b.subject && a.heading === b.heading && a.body === b.body && a.buttonLabel === b.buttonLabel;

const toPayload = (draft) => ({
  subject: draft.subject,
  heading: draft.heading,
  body: draft.body,
  buttonLabel: draft.buttonLabel.trim() ? draft.buttonLabel : null,
});

/** One notification's editor. Keeps its own unsaved draft. */
function TemplateEditor({ leagueId, template, onChanged }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { category, mergeFields, limits } = template;

  const saved = React.useMemo(() => toDraft(template.current), [template.current]); // eslint-disable-line react-hooks/exhaustive-deps
  const [draft, setDraft] = React.useState(saved);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [preview, setPreview] = React.useState(null);
  const [confirmReset, setConfirmReset] = React.useState(false);

  const inputs = React.useRef({});
  const lastFocused = React.useRef("body");

  // When the saved version changes (after save or reset), show it.
  React.useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const dirty = !sameDraft(draft, saved);
  const setField = (field) => (e) => {
    setNotice("");
    setDraft((d) => ({ ...d, [field]: e.target.value }));
  };

  // Adds {{field}} where the cursor is, in whichever box was used last.
  const insertField = (name) => {
    const field = lastFocused.current;
    const el = inputs.current[field];
    const token = `{{${name}}}`;
    setNotice("");
    setDraft((d) => {
      const value = d[field];
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? value.length;
      return { ...d, [field]: value.slice(0, start) + token + value.slice(end) };
    });
    const caret = (el?.selectionStart ?? 0) + token.length;
    setTimeout(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    }, 0);
  };

  const run = async (action) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onSave = () =>
    run(async () => {
      const updated = await saveEmailTemplate(leagueId, category, toPayload(draft));
      onChanged(updated);
      setNotice("Saved. New emails will use this wording.");
    });

  const onReset = () =>
    run(async () => {
      const updated = await resetEmailTemplate(leagueId, category);
      setConfirmReset(false);
      onChanged(updated);
      setNotice("Back to the default wording.");
    });

  const onPreview = () =>
    run(async () => {
      const result = await previewEmailTemplate(leagueId, category, toPayload(draft));
      if (!result.valid) {
        setError(result.errors.join(" "));
        return;
      }
      setPreview(result);
    });

  const field = (name, label, extra = {}) => (
    <TextField
      label={label}
      value={draft[name]}
      onChange={setField(name)}
      onFocus={() => {
        lastFocused.current = name;
      }}
      inputRef={(el) => {
        inputs.current[name] = el;
      }}
      fullWidth
      size="small"
      {...extra}
    />
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {template.description} Click a field to add it where your cursor is. Each one is replaced with the real details
        when the email is sent.
      </Typography>

      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
        {mergeFields.map((f) => (
          <Tooltip key={f.name} title={`${f.description}. Example: ${f.example}`} arrow>
            <Chip
              label={`{{${f.name}}}`}
              size="small"
              variant="outlined"
              onClick={() => insertField(f.name)}
              disabled={busy}
            />
          </Tooltip>
        ))}
      </Stack>

      {field("subject", "Subject", { helperText: `${draft.subject.length}/${limits.subject}` })}
      {field("heading", "Heading", { helperText: `${draft.heading.length}/${limits.heading}` })}
      {field("body", "Message", {
        multiline: true,
        minRows: 6,
        helperText: `Leave a blank line between paragraphs. ${draft.body.length}/${limits.body}`,
      })}
      {field("buttonLabel", "Button text", {
        helperText: "The button opens the player's schedule. Leave this empty to hide the button.",
      })}

      <Typography variant="caption" color="text.secondary">
        An unsubscribe link is always added at the bottom of every email.
      </Typography>

      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button variant="contained" onClick={onSave} disabled={busy || !dirty}>
          Save
        </Button>
        <Button variant="outlined" onClick={onPreview} disabled={busy}>
          Preview
        </Button>
        {template.isCustomized ? (
          <Button color="inherit" onClick={() => setConfirmReset(true)} disabled={busy}>
            Reset to default
          </Button>
        ) : null}
        {dirty ? (
          <Button color="inherit" onClick={() => setDraft(saved)} disabled={busy}>
            Discard changes
          </Button>
        ) : null}
      </Stack>

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>Preview</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            Subject
          </Typography>
          <Typography sx={{ mb: 1.5, fontWeight: 600 }}>{preview?.subject}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Shown with sample details (not a real player).
          </Typography>
          <Box
            component="iframe"
            title="Email preview"
            sandbox=""
            srcDoc={preview?.html ?? ""}
            sx={{ width: "100%", height: 480, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "#f3f4f6" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)}>
        <DialogTitle>Reset to the default wording?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your custom wording for &ldquo;{template.label}&rdquo; will be removed and the default will be used again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button color="error" onClick={onReset} disabled={busy}>
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * Manage League > Notifications. Lets owners and admins change the wording of the emails their league
 * sends. Only text is editable: the layout and unsubscribe link are added by the app.
 */
export default function EmailTemplatesEditor({ leagueId }) {
  const [templates, setTemplates] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    listEmailTemplates(leagueId)
      .then((data) => {
        if (!cancelled) setTemplates(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load your email templates.");
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const replace = (updated) =>
    setTemplates((list) => list.map((t) => (t.category === updated.category ? updated : t)));

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!templates) return <Typography variant="body2">Loading notifications...</Typography>;

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Notifications</Typography>
      <Typography variant="body2" color="text.secondary">
        Choose the wording of the notifications your league sends. Anything you leave alone uses the default.
      </Typography>

      <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
        Notifications are not instant. After something triggers one, like a player signing up, it can take up to 5
        minutes to be sent.
      </Alert>

      <Stack component="section" aria-labelledby="league-notifications-email-heading" spacing={1.5}>
        <Typography id="league-notifications-email-heading" variant="h6" component="h3">
          Email
        </Typography>
        {templates.map((t) => (
          <Accordion key={t.category} variant="outlined" disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 600 }}>{t.label}</Typography>
                <Chip size="small" label={t.isCustomized ? "Customized" : "Default"} color={t.isCustomized ? "primary" : "default"} variant={t.isCustomized ? "filled" : "outlined"} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <TemplateEditor leagueId={leagueId} template={t} onChanged={replace} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Stack>
  );
}
